
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { EpisodeWithCategory, EpisodeCategory } from "../types";

export const useEpisodeData = () => {
  const [episodes, setEpisodes] = useState<EpisodeWithCategory[]>([]);
  const [categories, setCategories] = useState<EpisodeCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchCategories = async () => {
    try {
      console.log("Fetching episode categories...");
      const { data, error } = await supabase
        .from("episodes_categories")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) {
        console.error("Error fetching categories:", error);
        throw error;
      }
      
      console.log("Categories fetched:", data?.length || 0);
      setCategories(data || []);
    } catch (error: any) {
      console.error("Categories fetch error:", error);
      toast({
        title: "Error",
        description: "Failed to fetch categories: " + error.message,
        variant: "destructive",
      });
    }
  };

  const fetchEpisodes = async () => {
    try {
      console.log("Fetching episodes...");
      const { data, error } = await supabase
        .from("episodes")
        .select(`
          *,
          episodes_categories!episodes_category_id_fkey (
            id,
            name,
            color
          )
        `)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching episodes:", error);
        throw error;
      }
      
      console.log("Episodes fetched:", data?.length || 0);
      
      // Add video_platform field with proper type casting
      const episodesWithPlatform = (data || []).map(episode => {
        const videoPlatform = episode.video_platform === "youtube" || episode.video_platform === "vimeo" 
          ? episode.video_platform 
          : (episode.vimeo_id ? "vimeo" as const : episode.youtube_id ? "youtube" as const : undefined);
        
        return {
          ...episode,
          video_platform: videoPlatform
        };
      });
      
      setEpisodes(episodesWithPlatform);
    } catch (error: any) {
      console.error("Episodes fetch error:", error);
      toast({
        title: "Error",
        description: "Failed to fetch episodes: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    episodes,
    categories,
    loading,
    setLoading,
    fetchCategories,
    fetchEpisodes,
  };
};
